import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Plus, Trash2, LayoutDashboard, Link2, ShieldCheck, AlertTriangle, CircleAlert, Loader2 } from "lucide-react";
import { normalizeDrillthrough, targetLabel } from "@/lib/drillthrough";

const TRIGGERS = [
  { v: "right_click", l: "Right click" },
  { v: "double_click", l: "Double click" },
  { v: "click", l: "Data point click" },
];

function TargetCard({ t, i, dashboards, currentId, sourceFields, onChange, onRemove }) {
  const [tf, setTf] = useState(null);
  useEffect(() => {
    if (t.mode !== "dashboard" || !t.dashboard_id) { setTf(null); return; }
    api.get(`/drillthrough/target-fields/${t.dashboard_id}`).then((r) => setTf(r.data)).catch(() => setTf({ fields: [] }));
  }, [t.mode, t.dashboard_id]);
  const set = (p) => onChange({ ...t, ...p });
  const setCarry = (k, v) => set({ carry: { ...(t.carry || {}), [k]: v } });
  const maps = t.mappings || [];
  const setMap = (j, p) => set({ mappings: maps.map((m, k) => (k === j ? { ...m, ...p } : m)) });
  return (
    <div data-testid={`dt-target-${i}`} className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-2">
      <div className="flex items-center gap-1.5">
        <Select value={t.mode} onValueChange={(v) => set({ mode: v })}>
          <SelectTrigger data-testid={`dt-target-${i}-mode`} className="h-7 w-[118px] text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="dashboard"><span className="flex items-center gap-1"><LayoutDashboard className="w-3 h-3" />Dashboard</span></SelectItem>
            <SelectItem value="url"><span className="flex items-center gap-1"><Link2 className="w-3 h-3" />External URL</span></SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid={`dt-target-${i}-label`} value={t.label} onChange={(e) => set({ label: e.target.value })} placeholder={targetLabel({ ...t, label: "" })} className="h-7 flex-1 text-[11px] bg-[color:var(--surface-2)]" aria-label="Target label" />
        <button data-testid={`dt-target-${i}-remove`} onClick={onRemove} title="Remove target" aria-label="Remove target" className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {t.mode === "dashboard" ? (
        <Select value={t.dashboard_id || ""} onValueChange={(v) => set({ dashboard_id: v, dashboard_name: (dashboards || []).find((d) => d.id === v)?.name })}>
          <SelectTrigger data-testid={`dt-target-${i}-dashboard`} className="h-8 text-xs bg-[color:var(--surface-2)]"><SelectValue placeholder="Pick a dashboard…" /></SelectTrigger>
          <SelectContent>
            {(dashboards || []).filter((d) => d.id !== currentId).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.status && d.status !== "published" ? ` · ${d.status}` : ""}</SelectItem>)}
            {dashboards && !dashboards.length && <SelectItem value="__none" disabled>No other dashboards</SelectItem>}
          </SelectContent>
        </Select>
      ) : (
        <div>
          <Input data-testid={`dt-target-${i}-url`} value={t.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://crm.example.com/accounts/{account_id}" className="h-8 text-xs bg-[color:var(--surface-2)]" aria-label="Target URL" />
          <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Tokens: {sourceFields.length ? sourceFields.map((f) => <code key={f} className="mr-1">{`{${f}}`}</code>) : "add dimensions to this widget first"}</p>
        </div>
      )}
      {tf && t.mode === "dashboard" && (
        <p data-testid={`dt-target-${i}-status`} className="text-[10px] text-[color:var(--ink-3)] flex items-center gap-1">
          <ShieldCheck className="w-3 h-3 text-[color:var(--brand-mint)]" />{tf.status === "published" ? "Published" : `Status: ${tf.status || "development"}`} · {tf.is_public ? "public link" : "signed-in only"} · {tf.fields?.length || 0} fields
        </p>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Open in</Label>
          <Select value={t.open} onValueChange={(v) => set({ open: v })}>
            <SelectTrigger data-testid={`dt-target-${i}-open`} className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="same_tab">Same tab</SelectItem>
              <SelectItem value="new_tab">New tab</SelectItem>
              <SelectItem value="modal">Modal / dialog</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Carry</Label>
          <div className="flex flex-col gap-0.5 mt-0.5">
            {[["clicked", "Clicked values"], ["widget_filters", "Widget filters"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-1.5 text-[10px] text-[color:var(--ink-2)]">
                <Checkbox data-testid={`dt-target-${i}-carry-${k}`} checked={t.carry?.[k] !== false} onCheckedChange={(v) => setCarry(k, !!v)} className="h-3 w-3" />{l}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Dashboard filters</Label>
          <Select value={t.filter_mode || "preserve"} onValueChange={(v) => set({ filter_mode: v, carry: { ...(t.carry || {}), global_filters: v !== "ignore" } })}>
            <SelectTrigger data-testid={`dt-target-${i}-filter-mode`} className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="preserve">Preserve (add to target's)</SelectItem>
              <SelectItem value="ignore">Ignore (don't carry)</SelectItem>
              <SelectItem value="override">Override target's filters</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Only when these carry a value</Label>
          <Select value="" onValueChange={(v) => set({ require_fields: [...new Set([...(t.require_fields || []), v])] })}>
            <SelectTrigger data-testid={`dt-target-${i}-require`} className="h-7 text-[11px] bg-[color:var(--surface-2)]">
              <span className="truncate text-[color:var(--ink-3)]">{(t.require_fields || []).length ? `${t.require_fields.length} required` : "Always available"}</span>
            </SelectTrigger>
            <SelectContent>{sourceFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {(t.require_fields || []).length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid={`dt-target-${i}-require-chips`}>
          {t.require_fields.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--surface-2)] text-[color:var(--ink-2)]">
              {f}
              <button data-testid={`dt-target-${i}-require-remove-${f}`} onClick={() => set({ require_fields: t.require_fields.filter((x) => x !== f) })}
                aria-label={`Stop requiring ${f}`} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">×</button>
            </span>
          ))}
        </div>
      )}
      <div>
        <Label className="text-[10px] text-[color:var(--ink-3)]">Page title (tokens allowed)</Label>
        <Input data-testid={`dt-target-${i}-title`} value={t.title || ""} onChange={(e) => set({ title: e.target.value })}
          placeholder="e.g. Sales detail — {region}" className="h-7 text-[11px] bg-[color:var(--surface-2)] mt-0.5" />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-[10px] text-[color:var(--ink-3)]">Field mapping (source → target)</Label>
          <button data-testid={`dt-target-${i}-add-map`} onClick={() => set({ mappings: [...maps, { source: sourceFields[0] || "", target: "" }] })} className="text-[10px] text-[color:var(--brand-ocean)] hover:underline flex items-center gap-0.5"><Plus className="w-3 h-3" />add</button>
        </div>
        {maps.map((m, j) => (
          <div key={j} className="flex items-center gap-1 mt-1">
            <Select value={m.source || ""} onValueChange={(v) => setMap(j, { source: v })}>
              <SelectTrigger data-testid={`dt-target-${i}-map-${j}-source`} className="h-7 flex-1 text-[10px] bg-[color:var(--surface-2)]"><SelectValue placeholder="source" /></SelectTrigger>
              <SelectContent>{sourceFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-[10px] text-[color:var(--ink-3)]">→</span>
            <Input data-testid={`dt-target-${i}-map-${j}-target`} list={`dt-tf-${i}`} value={m.target || ""} onChange={(e) => setMap(j, { target: e.target.value })} placeholder="target field" className="h-7 flex-1 text-[10px] bg-[color:var(--surface-2)]" />
            <button onClick={() => set({ mappings: maps.filter((_, k) => k !== j) })} aria-label="Remove mapping" className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <datalist id={`dt-tf-${i}`}>{(tf?.fields || []).map((f) => <option key={f} value={f} />)}</datalist>
        {!maps.length && <p className="text-[10px] text-[color:var(--ink-3)] mt-0.5">No mapping — fields keep their names on the target.</p>}
      </div>
    </div>
  );
}

// Item 13: multi-target drillthrough editor with modes, mappings, carry options and a validation check.
export function DrillthroughEditor({ widget, config, patchConfig }) {
  const { id: currentId } = useParams();
  const dt = normalizeDrillthrough(config.drillthrough);
  const [dashboards, setDashboards] = useState(null);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    if (!dt.enabled || dashboards) return;
    api.get("/dashboards").then(({ data }) => setDashboards((data || []).filter((d) => d.id !== currentId))).catch(() => setDashboards([]));
  }, [dt.enabled, dashboards, currentId]);
  const save = (next) => patchConfig({ drillthrough: next });
  const set = (patch) => save({ ...dt, ...patch });
  const sourceFields = [...new Set([...(config.dimensions || []).map((d) => d.field), ...(config.filters || []).map((f) => f.field)].filter(Boolean))];
  const addTarget = (mode) => set({ targets: [...dt.targets, { id: `t${Date.now().toString(36)}`, mode, label: "", dashboard_id: "", url: "", open: "same_tab", carry: { clicked: true, widget_filters: true, global_filters: true }, mappings: [] }] });
  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/drillthrough/validate", { id: currentId, widgets: [{ ...widget, config: { ...config, drillthrough: dt } }] });
      setCheck(data);
    } catch { setCheck(null); } finally { setChecking(false); }
  };
  return (
    <div className="pt-2 border-t border-[color:var(--glass-border)]" data-testid="drillthrough-editor">
      <div className="flex items-center justify-between gap-2 py-1">
        <Label className="text-sm text-[color:var(--ink-2)] flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" />Drillthrough</Label>
        <Switch data-testid="drillthrough-toggle" checked={dt.enabled} onCheckedChange={(v) => set({ enabled: v })} className="shrink-0" />
      </div>
      {dt.enabled && (
        <div className="space-y-2 mt-1">
          <p className="text-[10px] text-[color:var(--ink-3)]">Jump to other dashboards or external systems with the clicked values (and this widget's filters) applied. Several targets show a picker.</p>
          <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Trigger</Label>
            <Select value={dt.trigger} onValueChange={(v) => set({ trigger: v })}>
              <SelectTrigger data-testid="drillthrough-trigger" className="bg-[color:var(--surface)] mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
            {dt.trigger === "click" && <p className="text-[10px] text-[color:var(--warn,orange)] mt-1">Data point click replaces cross-filtering for this widget.</p>}
          </div>
          <div className="space-y-2" data-testid="drillthrough-targets">
            {dt.targets.map((t, i) => (
              <TargetCard key={t.id} t={t} i={i} dashboards={dashboards} currentId={currentId} sourceFields={sourceFields}
                onChange={(nt) => set({ targets: dt.targets.map((x, k) => (k === i ? nt : x)) })}
                onRemove={() => set({ targets: dt.targets.filter((_, k) => k !== i) })} />
            ))}
          </div>
          <div className="flex gap-1.5">
            <Button data-testid="drillthrough-add-dashboard" size="sm" variant="outline" className="h-7 text-[11px] flex-1 bg-[color:var(--surface)]" onClick={() => addTarget("dashboard")}><LayoutDashboard className="w-3 h-3 mr-1" />Add dashboard</Button>
            <Button data-testid="drillthrough-add-url" size="sm" variant="outline" className="h-7 text-[11px] flex-1 bg-[color:var(--surface)]" onClick={() => addTarget("url")}><Link2 className="w-3 h-3 mr-1" />Add URL</Button>
          </div>
          <div className="pt-1">
            <Button data-testid="drillthrough-check" size="sm" variant="outline" className="h-7 text-[11px] w-full bg-[color:var(--surface)]" onClick={runCheck} disabled={checking}>
              {checking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}Validate targets
            </Button>
            {check && (
              <div data-testid="drillthrough-check-result" className="mt-1.5 space-y-1">
                {check.issues.length === 0 && <p className="text-[10px] text-[color:var(--brand-mint)] flex items-center gap-1"><ShieldCheck className="w-3 h-3" />All targets are valid.</p>}
                {check.issues.map((it, k) => (
                  <div key={k} data-testid={`drillthrough-issue-${it.rule}`} className="text-[10px] rounded-lg bg-[color:var(--surface-2)] px-2 py-1 flex items-start gap-1.5">
                    {it.severity === "error" ? <CircleAlert className="w-3 h-3 mt-0.5 text-[color:var(--danger)] shrink-0" /> : <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />}
                    <span><span className="text-[color:var(--ink-2)]">{it.message}</span> <span className="text-[color:var(--ink-3)]">{it.fix}</span></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
